import api from './config/api';
import axios from './config/axios';

/**
 * Transform a list of location's by adding a 'friendly' version of that location's name
 * @param data { Object }
 * @param locationListAttribute { String }
 * @param locationListLocationNameAttribute { String }
 * @returns { Object }
 */
const locationListFriendlyNameTransformer = ( data, locationListAttribute, locationListLocationNameAttribute ) => ( {
    ...data,
    [ locationListAttribute ]: ( data[ locationListAttribute ] && data[ locationListAttribute ].length && ( data[ locationListAttribute ].length > 0 ) )
        ? data[ locationListAttribute ].map( ( location ) => {
            // Removes the trailing dash and number from a location name. ( Eg: 'College Name Bookstore - 99' to 'College Name Bookstore' )
            let friendly = location[ locationListLocationNameAttribute ].replace( / -([^-]+)$/, '' ).trim();
            // Strip 'Campus Store' from the friendly if it exists
            if ( friendly.includes( 'Campus Store' ) ) friendly = friendly.split( 'Campus Store' )[ 0 ].trim();
            // Strip 'Bookstore' from the friendly if it exists
            if ( friendly.includes( 'Bookstore' ) ) friendly = friendly.split( 'Bookstore' )[ 0 ].trim();
            // Return the location object with a new 'friendly' attribute
            return {
                ...location,
                friendly,
            };
        } )
        : data[ locationListAttribute ],
} );

export default {
    /**
     * Get an array of all possible mail-related permissions ( actions )
     * @param jwtToken
     * @param websiteId
     * @param userAccountId
     * @param username
     * @returns { AxiosPromise }
     */
    getAllMailRoles( {
        jwtToken,
        websiteId,
        userAccountId,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/mail/permissions` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
        } );
    },

    /**
     * Get an array of mail-related permissions ( actions ) for a given user by username
     * @param jwtToken
     * @param userAccountId
     * @param username
     * @returns { AxiosPromise }
     */
    getUserMailPermissions( {
        jwtToken,
        userAccountId,
        username,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ userAccountId }/mail/permissions/user` ),
            method: 'get',
            headers: api.authHeaders( jwtToken ),
            params: {
                username,
            },
            transformResponse: axios.defaults.transformResponse
                .concat( ( data ) => locationListFriendlyNameTransformer( data, 'locationPermissionsList', 'locationName' ) ),
        } );
    },

    /**
     * Modify mail-related permissions
     * @param jwtToken
     * @param websiteId
     * @param userAccountId
     * @param modifiedPermissions { Object } - that looks like the following:
     *    {
     *        username: { String },
     *        mailPermissionsList: [
     *              {
     *                  locationId: { Number },
     *                  mode: { String }, 'add' or 'remove'
     *                  roleNames: [ 'MAIL_OPERATOR', 'MAIL_ADMINISTRATOR' ],
     *              },
     *        ],
     *    }
     * @returns { AxiosPromise }
     */
    modifyUserMailPermissions( {
        jwtToken,
        websiteId,
        userAccountId,
        modifiedPermissions,
    } ) {
        return axios( {
            url: api.logisticsUrl( `${ websiteId }/${ userAccountId }/mail/permissions/user/modify` ),
            method: 'post',
            headers: api.authHeaders( jwtToken ),
            data: {
                ...modifiedPermissions,
            },
        } );
    },

    /**
     * Get a list of locations
     * @param username
     * @param password
     * @returns { AxiosPromise }
     */
    getLocationList( {
        username,
        password,
    } ) {
        return axios( {
            url: api.cmaUrl( 'titleResearch/location/list' ),
            method: 'get',
            headers: {
                Authorization: `Basic ${ btoa( `${ username }:${ password }` ) }`,
            },
            transformResponse: axios.defaults.transformResponse
                .concat( ( data ) => locationListFriendlyNameTransformer( data, 'locations', 'name' ) ),
        } );
    },
};
